import { useTranslation } from 'react-i18next';
import { Moon, Smartphone, Sun } from 'lucide-react';
import { cn } from '@/lib/utils';
import { THEME_PREFERENCES, type ThemePreference } from '@/lib/theme';
import { setTheme } from '@/Redux/Slices/uiSlice';
import { useAppDispatch, useAppSelector } from '@/Redux/store';

const ICONS: Record<ThemePreference, typeof Sun> = {
  system: Smartphone,
  light: Sun,
  dark: Moon,
};

/**
 * Three states, not a switch.
 *
 * A two-way toggle has no way to say "follow my phone", so the moment a guest
 * touches it they are opted out of their own dark-mode schedule for good. The
 * third segment is the default.
 *
 * Rendered as radios so the whole group is one tab stop and arrow keys move
 * between the options — and so the selected state is announced, which an
 * icon-only row of buttons cannot do.
 */
export function ThemeToggle({ className }: { className?: string }) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const preference = useAppSelector((s) => s.ui.theme);

  return (
    <div
      role="radiogroup"
      aria-label={t('guest.theme.label')}
      className={cn(
        'inline-flex items-center gap-0.5 rounded-lg border border-line bg-surface p-0.5',
        className,
      )}
    >
      {THEME_PREFERENCES.map((option) => {
        const Icon = ICONS[option];
        const selected = preference === option;
        return (
          <button
            key={option}
            type="button"
            role="radio"
            aria-checked={selected}
            /* The icon alone is ambiguous — a phone glyph is not obviously
               "match my device" — so every segment carries its own label. */
            aria-label={t(`guest.theme.${option}`)}
            title={t(`guest.theme.${option}`)}
            onClick={() => dispatch(setTheme(option))}
            className={cn(
              'grid h-9 w-11 place-items-center rounded-md transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              selected
                ? 'bg-accent text-on-accent'
                : 'text-muted-foreground hover:bg-card hover:text-foreground',
            )}
          >
            <Icon className="h-4 w-4" aria-hidden="true" />
          </button>
        );
      })}
    </div>
  );
}
